import type { RefCallback } from 'react';
import { useEffect, useState } from 'react';

import { useTableZoom } from './TableZoomContext';

// Exponential so each unit of wheel travel changes the zoom by the same ratio in both directions.
const WHEEL_ZOOM_SENSITIVITY = 0.002;
// Mouse wheel notches deliver about 100px per event; pinch deltas are single digits. Capping keeps
// one notch to a few percent instead of jumping to the range limits.
const MAX_WHEEL_DELTA_PX = 40;
const LINE_HEIGHT_PX = 16;
const PAGE_HEIGHT_PX = 800;

const wheelDeltaInPixels = (event: WheelEvent): number => {
    switch (event.deltaMode) {
        case WheelEvent.DOM_DELTA_LINE:
            return event.deltaY * LINE_HEIGHT_PX;
        case WheelEvent.DOM_DELTA_PAGE:
            return event.deltaY * PAGE_HEIGHT_PX;
        default:
            return event.deltaY;
    }
};

// Trackpad pinch arrives as ctrl+wheel in every browser. Cmd+wheel is the macOS mouse equivalent
// of ctrl+wheel and must be caught too, or it falls through to the browser's page zoom.
const isZoomGesture = (event: WheelEvent): boolean => event.ctrlKey || event.metaKey;

// Ctrl/Cmd+wheel and pinch zoom the table instead of the page. Registered natively and non-passive
// because React's wheel listener is passive and cannot cancel the browser zoom.
export const useTableZoomGesture = (): RefCallback<HTMLElement> => {
    const { enabled, setZoom } = useTableZoom();
    const [element, setElement] = useState<HTMLElement | null>(null);

    useEffect(() => {
        if (element === null || !enabled) return;

        const handleWheel = (event: WheelEvent): void => {
            if (!isZoomGesture(event)) return;

            event.preventDefault();
            const delta = Math.max(-MAX_WHEEL_DELTA_PX, Math.min(MAX_WHEEL_DELTA_PX, wheelDeltaInPixels(event)));
            setZoom((current) => current * Math.exp(-delta * WHEEL_ZOOM_SENSITIVITY));
        };

        element.addEventListener('wheel', handleWheel, { passive: false });

        return () => element.removeEventListener('wheel', handleWheel);
    }, [element, enabled, setZoom]);

    return setElement;
};
